import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, X, FileText, Download, Eye, Filter, RefreshCw } from "lucide-react";
import { adminListSubmissions, adminGetSubmission, openSubmissionPdf, exportSubmissions, recalculateProfiles } from "../../riskprofiler/riskApi";

const PROFILES = ["Conservative", "Moderate", "Aggressive"];
const badge = (p) => ({
    Conservative: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Moderate: "bg-blue-50 text-oth-blue border-blue-200",
    Aggressive: "bg-amber-50 text-[#b45309] border-amber-200",
}[p] || "bg-oth-line/30 text-oth-ink2 border-oth-line");

const Card = ({ label, value, testId }) => (
    <div className="rounded-2xl border border-oth-line/70 bg-white px-4 py-3" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{label}</div>
        <div className="font-display text-2xl font-semibold tracking-tight mt-0.5">{value}</div>
    </div>
);

const DetailModal = ({ id, onClose }) => {
    const [d, setD] = useState(null);
    useEffect(() => { adminGetSubmission(id).then(setD).catch(() => toast.error("Could not load submission.")); }, [id]);
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="rp-detail-modal">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 md:p-8 shadow-2xl max-h-[88vh] overflow-y-auto thin-scroll">
                <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3 hover:text-oth-ink" data-testid="rp-detail-close"><X size={16} /></button>
                {!d ? <div className="font-editorial italic text-oth-ink3 py-10 text-center">Loading…</div> : (
                    <>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-oth-blue font-body mb-1">Submission</div>
                        <h3 className="font-display text-2xl font-semibold tracking-tight">{d.name}</h3>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm font-body">
                            <div><span className="text-oth-ink3">Email</span><div className="text-oth-ink">{d.email}</div></div>
                            <div><span className="text-oth-ink3">Age</span><div className="text-oth-ink">{d.age}</div></div>
                            <div><span className="text-oth-ink3">Date</span><div className="text-oth-ink">{d.date_label}</div></div>
                            <div><span className="text-oth-ink3">Risk Profile</span><div><span className={`inline-block px-2.5 py-0.5 rounded-full text-xs border ${badge(d.profile)}`}>{d.profile}</span></div></div>
                        </div>
                        <div className="mt-5 flex items-center gap-2">
                            <button onClick={() => openSubmissionPdf(d.id, d.name, false)} className="btn-pill btn-pill-light text-sm" data-testid="rp-detail-view-pdf"><Eye size={14} /> View PDF</button>
                            <button onClick={() => openSubmissionPdf(d.id, d.name, true)} className="btn-pill btn-pill-dark text-sm" data-testid="rp-detail-download-pdf"><Download size={14} /> Download PDF</button>
                        </div>
                        <div className="mt-6 space-y-4">
                            {d.answers.map((a, i) => (
                                <div key={i} className="rounded-2xl border border-oth-line/70 p-4">
                                    <div className="font-body text-sm font-medium text-oth-ink">{i + 1}. {a.question_text}</div>
                                    <div className="font-body text-sm text-oth-ink2 mt-1.5">Selected: <strong>{a.selected_option}.</strong> {a.selected_answer_text}</div>
                                    <div className="font-body text-xs text-oth-ink3 mt-1">Score: {a.score}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-5 flex items-center justify-between rounded-2xl bg-[#f0f0ec] px-5 py-4 font-display font-semibold text-oth-blue">
                            <span>Total Score: {d.total_score} / {d.max_score}</span>
                            <span>{d.profile}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const fieldCls = "bg-white border border-oth-line rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue";

export function RiskResponsesTab() {
    const [data, setData] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [f, setF] = useState({ search: "", profile: "", from: "", to: "", min_score: "", max_score: "" });
    const [sort, setSort] = useState({ sort: "date", dir: "desc" });
    const [recalcBusy, setRecalcBusy] = useState(false);

    const load = useCallback(async () => {
        const params = { ...sort };
        Object.entries(f).forEach(([k, v]) => { if (v !== "") params[k] = v; });
        try { setData(await adminListSubmissions(params)); }
        catch (e) { toast.error("Could not load submissions."); }
    }, [f, sort]);

    useEffect(() => { load(); }, [load]);

    const a = data?.analytics || {};
    const clear = () => setF({ search: "", profile: "", from: "", to: "", min_score: "", max_score: "" });
    const setSortKey = (key) => setSort((s) => ({ sort: key, dir: s.sort === key && s.dir === "desc" ? "asc" : "desc" }));
    const params = () => { const p = { ...sort }; Object.entries(f).forEach(([k, v]) => { if (v !== "") p[k] = v; }); return p; };

    const recalc = async () => {
        if (!window.confirm("Recalculate the risk profile tag for ALL submissions (including older ones) using the current scoring and thresholds? Question wording stays unchanged.")) return;
        setRecalcBusy(true);
        try { const r = await recalculateProfiles(); toast.success(`Recalculated ${r.updated} submission(s).`); load(); }
        catch { toast.error("Could not recalculate."); }
        finally { setRecalcBusy(false); }
    };

    return (
        <div data-testid="rp-responses-tab">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                <Card label="Total" value={a.total ?? "—"} testId="rp-card-total" />
                <Card label="Conservative" value={a.conservative ?? "—"} testId="rp-card-cons" />
                <Card label="Moderate" value={a.moderate ?? "—"} testId="rp-card-mod" />
                <Card label="Aggressive" value={a.aggressive ?? "—"} testId="rp-card-aggr" />
                <Card label="Avg Score" value={a.average_score ?? "—"} testId="rp-card-avgscore" />
                <Card label="Avg Age" value={a.average_age ?? "—"} testId="rp-card-avgage" />
            </div>

            <div className="rounded-2xl border border-oth-line/70 bg-white p-4 mb-5">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-3"><Filter size={13} /> Filters</div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-2.5 text-oth-ink3" />
                        <input className={`${fieldCls} pl-8`} placeholder="Search name or email" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} data-testid="rp-filter-search" />
                    </div>
                    <select className={fieldCls} value={f.profile} onChange={(e) => setF({ ...f, profile: e.target.value })} data-testid="rp-filter-profile">
                        <option value="">All profiles</option>
                        {PROFILES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <input type="date" className={fieldCls} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} data-testid="rp-filter-from" />
                    <input type="date" className={fieldCls} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="rp-filter-to" />
                    <input type="number" className={`${fieldCls} w-24`} placeholder="Min score" value={f.min_score} onChange={(e) => setF({ ...f, min_score: e.target.value })} data-testid="rp-filter-min" />
                    <input type="number" className={`${fieldCls} w-24`} placeholder="Max score" value={f.max_score} onChange={(e) => setF({ ...f, max_score: e.target.value })} data-testid="rp-filter-max" />
                    <button onClick={clear} className="px-3 py-2 rounded-xl border border-oth-line text-sm font-body text-oth-ink2 hover:bg-[#f0f0ec]" data-testid="rp-filter-clear">Clear Filters</button>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={recalc} disabled={recalcBusy} className="btn-pill btn-pill-blue text-sm disabled:opacity-60" data-testid="rp-recalculate"><RefreshCw size={14} className={recalcBusy ? "animate-spin" : ""} /> {recalcBusy ? "Recalculating…" : "Recalculate Profiles"}</button>
                        <button onClick={() => exportSubmissions(params(), false)} className="btn-pill btn-pill-light text-sm" data-testid="rp-export-summary"><Download size={14} /> Export CSV</button>
                        <button onClick={() => exportSubmissions(params(), true)} className="btn-pill btn-pill-dark text-sm" data-testid="rp-export-detailed"><Download size={14} /> Export Detailed</button>
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                <div className="overflow-x-auto thin-scroll">
                    <table className="w-full text-sm font-body" data-testid="rp-responses-table">
                        <thead>
                            <tr className="bg-[#0A0A0A] text-white text-left text-[11px] uppercase tracking-[0.1em]">
                                {[["name", "Name"], [null, "Email"], [null, "Age"], ["score", "Score"], ["profile", "Risk Profile"], [null, "# Q"], [null, "PDF"]].map(([key, label], i) => (
                                    <th key={i} className={`px-4 py-3 ${key ? "cursor-pointer select-none" : ""}`} onClick={key ? () => setSortKey(key) : undefined} data-testid={key ? `rp-sort-${key}` : undefined}>
                                        {label}{sort.sort === key ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.items || []).map((s) => (
                                <tr key={s.id} className="border-b border-oth-line/40 hover:bg-[#fafaf8] cursor-pointer" onClick={() => setOpenId(s.id)} data-testid={`rp-row-${s.id}`}>
                                    <td className="px-4 py-3 font-medium text-oth-ink">{s.name}</td>
                                    <td className="px-4 py-3 text-oth-ink2">{s.email}</td>
                                    <td className="px-4 py-3">{s.age}</td>
                                    <td className="px-4 py-3">{s.total_score}/{s.max_score}</td>
                                    <td className="px-4 py-3"><span className={`inline-block px-2.5 py-0.5 rounded-full text-xs border ${badge(s.profile)}`}>{s.profile}</span></td>
                                    <td className="px-4 py-3">{s.question_count}</td>
                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-1.5">
                                            <button onClick={() => openSubmissionPdf(s.id, s.name, false)} title="View PDF" className="w-8 h-8 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 hover:bg-[#f0f0ec]" data-testid={`rp-pdf-view-${s.id}`}><FileText size={14} /></button>
                                            <button onClick={() => openSubmissionPdf(s.id, s.name, true)} title="Download PDF" className="w-8 h-8 rounded-lg border border-oth-line flex items-center justify-center text-oth-ink2 hover:bg-[#f0f0ec]" data-testid={`rp-pdf-dl-${s.id}`}><Download size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {data && data.items.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center font-editorial italic text-oth-ink3" data-testid="rp-empty">No submissions match these filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {openId && <DetailModal id={openId} onClose={() => setOpenId(null)} />}
        </div>
    );
}
